"use client";

import React, {useState, useRef, useEffect} from "react";
import {useRouter} from "next/navigation";
import {useAuthenticator} from "@aws-amplify/ui-react";
import {Menu, X, BookOpen, LogOut, Bug, Mail, HelpCircle, Users, FileText, Play, Trophy} from "lucide-react";

export default function BurgerMenu({onFeedbackClick}: { onFeedbackClick?: () => void }) {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const router = useRouter();
    const {signOut, authStatus} = useAuthenticator(
        (context) => [context.signOut, context.authStatus]
    );

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const menuItems = [
        {
            icon: Play,
            label: "On Demand",
            description: "Access On Demand content",
            onClick: () => {
                router.push("/playback");
                setIsOpen(false);
            },
        },
        {
            icon: Trophy,
            label: "Leaderboard",
            description: "View the performance leaderboard",
            onClick: () => {
                router.push("/leaderboard");
                setIsOpen(false);
            },
        },
        {
            icon: HelpCircle,
            label: "FAQs",
            description: "Frequently asked questions",
            onClick: () => {
                router.push("/faq");
                setIsOpen(false);
            },
        },
        {
            icon: BookOpen,
            label: "Quick Start Guide",
            description: "Learn how to use WiderTalk",
            onClick: () => {
                router.push("/quick-start");
                setIsOpen(false);
            },
        },
        {
          icon: FileText,
          label: "Blog",
          description: "Latest news and insights",
          onClick: () => {
            router.push("/blog");
            setIsOpen(false);
          },
        },
        {
            icon: Users,
            label: "Our Team",
            description: "Meet the people behind WiderTalk",
            onClick: () => {
                router.push("/team");
                setIsOpen(false);
            },
        },
        {
            icon: Mail,
            label: "Contact Us",
            description: "Get in touch with our team",
            onClick: () => {
                router.push("/contact");
                setIsOpen(false);
            },
        },
        {
            icon: Bug,
            label: "Submit Feedback & Bugs",
            description: "Help us improve WiderTalk",
            onClick: () => {
                onFeedbackClick?.();
                setIsOpen(false);
            },
        },
    ];

    // Add logout option for authenticated users
    if (authStatus === "authenticated") {
        menuItems.push({
            icon: LogOut,
            label: "Logout",
            description: "Sign out of your account",
            onClick: () => {
                signOut();
                router.push("/");
                setIsOpen(false);
            },
        });
    }

    return (
        <div className="relative" ref={menuRef}>
            {/* Burger Menu Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center rounded-lg p-2 w-10 h-10 hover:bg-widertalk-navy/20 text-widertalk-navy transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-widertalk-navy focus:ring-offset-2 cursor-pointer"
                aria-label="Menu"
            >
                {isOpen ? <X size={20}/> : <Menu size={20}/>}
            </button>

            {/* Menu Dropdown */}
            {isOpen && (
                <div
                    className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-[1001]">
                    {menuItems.map((item, index) => {
                        const IconComponent = item.icon;
                        return (
                            <button
                                key={index}
                                onClick={item.onClick}
                                className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-200 flex items-start gap-3"
                            >
                                <div className="mt-1">
                                    <IconComponent size={18} className="text-widertalk-navy"/>
                                </div>
                                <div>
                                    <div className="font-medium text-widertalk-navy text-sm">
                                        {item.label}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                        {item.description}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
